import {
  TIME_RANGE_OPTIONS,
  RANGE_MODE_OPTIONS,
  compareCaption,
} from "./TimeRangeControls.helpers";
import { useEffect, useMemo, useRef, useState } from "react";
import type { AnalyticsPeriod, CustomDateRange, RangeMode, TimeRange } from "@/lib/api";
import { todayNzIso } from "@/lib/nz-time";
import { clampCustomStart, customRangeExpired, retentionFloorIso } from "@/lib/retention";

// Task #425 — the default Calendar/Rolling mode used by every view's
// initial state. Rolling wins for the ranges that support it; the
// degenerate ranges (daily / all_time / custom) force calendar
// internally regardless (see ``effectiveRangeMode`` in each consumer).
// Exported alongside the options list (whose visual order is
// Calendar-first and unchanged) so consumers share one default.
export const DEFAULT_RANGE_MODE: RangeMode = "rolling";

export type TimeRangeControlsCompareProps = {
  compare: boolean;
  period: AnalyticsPeriod;
  priorPeriod: AnalyticsPeriod | null;
  onCompareChange: (v: boolean) => void;
};

// Validate a picker pair before allowing Apply. Returns an error string
// when the pair is invalid (so the UI can surface it), or null when the
// pair is good to send. Mirrors the server-side parse_custom_window().
function validateCustomPair(
  start: string,
  end: string,
): string | null {
  if (!start || !end) return "Pick a start and end date";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(start) || !/^\d{4}-\d{2}-\d{2}$/.test(end))
    return "Dates must be in YYYY-MM-DD format";
  const s = new Date(`${start}T00:00:00Z`);
  const e = new Date(`${end}T00:00:00Z`);
  if (Number.isNaN(s.getTime()) || Number.isNaN(e.getTime()))
    return "Invalid date";
  if (e < s) return "End date must be on or after start date";
  const today = new Date(`${todayNzIso()}T00:00:00Z`);
  if (e > today) return "End date can't be in the future";
  return null;
}

export function TimeRangeControls({
  value,
  rangeMode,
  customRange,
  onValueChange,
  onRangeModeChange,
  onCustomRangeChange,
  compare: compareProps,
}: {
  value: TimeRange;
  rangeMode: RangeMode;
  // Task #365 — current custom window (or null when not yet picked).
  // The parent owns the source-of-truth; controls report changes via
  // onCustomRangeChange. Required when "custom" is selectable.
  customRange?: CustomDateRange | null;
  onValueChange: (v: TimeRange) => void;
  onRangeModeChange: (v: RangeMode) => void;
  // Called when the user picks a valid pair and clicks Apply. When the
  // user picks "Custom" from the preset toggle without a stored range,
  // the parent should leave customRange as null until this fires —
  // we'll auto-open the picker.
  onCustomRangeChange?: (range: CustomDateRange) => void;
  // The "Compare to prior" toggle (and its surrounding caption) is
  // optional so callers like the Agents screen can omit it entirely.
  compare?: TimeRangeControlsCompareProps;
}) {
  // All-time has no equivalent prior period to compare against, so the
  // toggle is disabled and forced off when that range is active.
  const compareDisabled = value === "all_time";
  // The Calendar/Rolling selector frames the *current* period, so it
  // shows whenever the range supports both interpretations — Daily,
  // All-time, and Custom are degenerate and don't need the choice.
  const showModeSelector =
    value !== "daily" && value !== "all_time" && value !== "custom";
  const caption =
    compareProps && compareProps.compare && !compareDisabled
      ? compareCaption(rangeMode, compareProps.period, compareProps.priorPeriod)
      : null;

  // Task #365 — custom picker UI. Controlled locally so users can edit
  // both dates before Apply commits them to the parent. We seed from
  // ``customRange`` when present and reset whenever the parent's range
  // changes (e.g. after a deep link).
  const today = useMemo(todayNzIso, []);
  const retentionFloor = useMemo(() => retentionFloorIso(today), [today]);
  const [pickerOpen, setPickerOpen] = useState(false);
  // A deep link can carry a start before the floor. Clamp on the way *in*, so
  // the draft never holds a date the Start input's own `min` forbids — and so
  // the End input, whose `min` follows draftStart, can't inherit it either.
  // A window that *ends* before the floor has no overlap with retained data
  // at all: it seeds nothing (and renders as the placeholder below) rather
  // than an inverted, self-invalid pair.
  const rangeExpired =
    !!customRange?.end && customRangeExpired(customRange.end, retentionFloor);
  const seededStart =
    !rangeExpired && customRange?.start
      ? clampCustomStart(customRange.start, retentionFloor)
      : "";
  const seededEnd = (!rangeExpired && customRange?.end) || "";
  const [draftStart, setDraftStart] = useState<string>(seededStart);
  const [draftEnd, setDraftEnd] = useState<string>(seededEnd);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  // Re-sync the draft when the parent's window changes (deep link, or
  // a different view writes its own custom range).
  useEffect(() => {
    setDraftStart(seededStart);
    setDraftEnd(seededEnd);
  }, [seededStart, seededEnd]);
  // Close the popover on outside click so it behaves like a typical
  // menu (Escape and the explicit Cancel button also close it).
  useEffect(() => {
    if (!pickerOpen) return;
    function onDocClick(e: MouseEvent) {
      const node = popoverRef.current;
      if (node && !node.contains(e.target as Node)) setPickerOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setPickerOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [pickerOpen]);

  const draftError =
    draftStart || draftEnd ? validateCustomPair(draftStart, draftEnd) : null;
  const canApply = !draftError && !!draftStart && !!draftEnd;

  function handlePresetClick(next: TimeRange) {
    if (next === "custom") {
      // Open the picker immediately so the user can complete the
      // selection. If a previous custom range exists we still commit
      // it as the active value so cards re-fetch with the existing
      // dates; otherwise the parent stays on the previous range until
      // the user clicks Apply.
      setPickerOpen(true);
      if (customRange?.start && customRange?.end) {
        onValueChange("custom");
      }
      return;
    }
    setPickerOpen(false);
    onValueChange(next);
  }

  function handleApply() {
    if (!canApply || !onCustomRangeChange) return;
    onCustomRangeChange({ start: draftStart, end: draftEnd });
    // Switching to "custom" here covers the first-time selection where
    // the parent's ``value`` is still the previous preset.
    if (value !== "custom") onValueChange("custom");
    setPickerOpen(false);
  }

  return (
    <div className="flex items-center gap-3 flex-wrap">
      {/* Task #365 — wrap the toggle group + custom-range popover in
          a single relative container so the popover can escape the
          toggle group's overflow-hidden clipping (which is needed to
          preserve the rounded-corner pill look) while still anchoring
          to the right edge of the group. */}
      <div className="relative">
        <div
          role="group"
          aria-label="Time range"
          className="inline-flex rounded-md border border-input bg-card overflow-hidden"
        >
          {TIME_RANGE_OPTIONS.map((opt) => {
            const active = opt.value === value;
            const isCustom = opt.value === "custom";
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => handlePresetClick(opt.value)}
                className={`px-3 py-1.5 text-sm border-r border-border last:border-r-0 ${
                  active
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground hover:bg-muted/40"
                }`}
                aria-pressed={active}
                aria-haspopup={isCustom ? "dialog" : undefined}
                aria-expanded={isCustom ? pickerOpen : undefined}
              >
                {isCustom && active && !rangeExpired && customRange?.start && customRange?.end
                  ? `${clampCustomStart(customRange.start, retentionFloor)} – ${customRange.end}`
                  : opt.label}
              </button>
            );
          })}
        </div>
        {pickerOpen ? (
          <div
            ref={popoverRef}
            role="dialog"
            aria-label="Pick a custom date range"
            className="absolute right-0 top-full z-30 mt-1 w-72 rounded-md border border-input bg-popover p-3 shadow-md text-popover-foreground"
          >
            <div className="flex flex-col gap-2 text-xs">
              <label className="flex flex-col gap-1">
                <span>Start (NZ time)</span>
                <input
                  type="date"
                  value={draftStart}
                  min={retentionFloor}
                  max={draftEnd || today}
                  onChange={(e) => setDraftStart(e.target.value)}
                  className="rounded border border-input bg-background px-2 py-1 text-sm"
                />
              </label>
              <label className="flex flex-col gap-1">
                <span>End (NZ time, inclusive)</span>
                <input
                  type="date"
                  value={draftEnd}
                  min={draftStart || retentionFloor}
                  max={today}
                  onChange={(e) => setDraftEnd(e.target.value)}
                  className="rounded border border-input bg-background px-2 py-1 text-sm"
                />
              </label>
              {draftError ? (
                <p className="text-destructive" role="alert">
                  {draftError}
                </p>
              ) : null}
              <div className="mt-1 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setPickerOpen(false)}
                  className="rounded border border-input px-2 py-1 hover:bg-muted/40"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!canApply}
                  onClick={handleApply}
                  className="rounded bg-primary px-2 py-1 text-primary-foreground disabled:opacity-50"
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
      {compareProps ? (
        <label
          className={`inline-flex items-center gap-1.5 text-xs ${compareDisabled ? "text-muted-foreground" : "text-foreground"}`}
          title={
            compareDisabled
              ? "Last 12 months has no prior period to compare against"
              : "Show change vs the equivalent prior period"
          }
        >
          <input
            type="checkbox"
            checked={compareProps.compare && !compareDisabled}
            disabled={compareDisabled}
            onChange={(e) => compareProps.onCompareChange(e.target.checked)}
            className="rounded border-input"
          />
          Compare to prior
        </label>
      ) : null}
      {caption ? (
        <span
          className="text-xs text-muted-foreground tabular-nums"
          aria-live="polite"
          title="Current period vs equivalent prior period"
        >
          {caption}
        </span>
      ) : null}
      {showModeSelector ? (
        <div
          role="group"
          aria-label="Comparison mode"
          className="inline-flex rounded-md border border-input bg-card overflow-hidden"
        >
          {RANGE_MODE_OPTIONS.map((opt) => {
            const active = opt.value === rangeMode;
            return (
              <button
                key={opt.value}
                type="button"
                title={opt.tip}
                onClick={() => onRangeModeChange(opt.value)}
                className={`px-2.5 py-1 text-xs border-r border-border last:border-r-0 ${
                  active
                    ? "bg-secondary text-secondary-foreground"
                    : "text-muted-foreground hover:bg-muted/40"
                }`}
                aria-pressed={active}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
